import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { IDevice, fetchDevices } from '../../../../libraries/helpdesk/devices';
import { useEffect, useRef, useState } from 'react';
import Header from '../../../components/util/header';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { Group, Loader, Table } from '@mantine/core';
import PaginationComponent from '../../../components/pagination';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

const HelpdeskDevices = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = useRef(new AbortController()).current;
  const [list, setList] = useState<IDevice[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });

  //const eidParam = searchParams.get('eid');
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadDevices();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadDevices = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    setLoading(true);
    fetchDevices(
      controller,
      filterBarData?.devId,
      filterBarData?.eid,
      filterBarData?.sn,
      filterBarData?.codArm,
      filterBarData?.productCode
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data.devices || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  return (
    <div className="devices">
      <Header pageTitle="Devices" />

      <HelpdeskFilterBar
        onSearch={loadDevices}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          eid: true,
          sn: true,
          devId: true,
          codArm: true,
          productCode: true
        }}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>DevID</Table.Th>
              <Table.Th>SN</Table.Th>
              <Table.Th>{lang.HELPDESK_PRODUCT_CODE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_ID_MIN}</Table.Th>
              <Table.Th>{lang.GLOBAL_WAREHOUSE}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={12}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={12} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((device, index) => (
                    <Table.Tr key={index}>
                      <Table.Td>{device.devID}</Table.Td>
                      <Table.Td>{device.sn}</Table.Td>
                      <Table.Td>{device.productCode}</Table.Td>
                      <Table.Td>{device.eid}</Table.Td>
                      <Table.Td>{device.codArm}</Table.Td>
                    </Table.Tr>
                  ))
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          currentPage={currentPage}
        />
      </Group>
    </div>
  );
};

export default HelpdeskDevices;
